import { z } from 'zod';

/** The campaign page's tabs (ADR-318, H), in the URL as `?tab=`. */
export const CAMPAIGN_TABS = ['queue', 'results', 'setup'] as const;
export type CampaignTab = (typeof CAMPAIGN_TABS)[number];

export interface CampaignPageSearch {
  tab?: CampaignTab;
  /** The referral (its task id) open in a slide-over over the queue. */
  referral?: string;
  /** The patient open in a slide-over over the queue. */
  patient?: string;
  /**
   * A Ready referral (its task id) open in the slots panel: the patient beside
   * the times Iris would offer them (ADR-341).
   */
  slots?: string;
  /**
   * The call open over the referral. Written by `useCallDetailParam` from
   * inside the referral panel; declared here or the router strips it.
   */
  call?: string;
}

const uuid = z.string().uuid();
/** An id from the URL, or nothing: a hand-edited one never reaches a fetch. */
const idOf = (value: unknown): string | undefined =>
  uuid.safeParse(value).success ? (value as string) : undefined;

export function campaignPageSearch(raw: Record<string, unknown>): CampaignPageSearch {
  const referral = idOf(raw.referral);
  // One slide-over at a time: a referral first, then the slots panel, then a
  // patient.
  const slots = referral ? undefined : idOf(raw.slots);
  return {
    tab: (CAMPAIGN_TABS as readonly unknown[]).includes(raw.tab)
      ? (raw.tab as CampaignTab)
      : undefined,
    referral,
    patient: referral || slots ? undefined : idOf(raw.patient),
    slots,
    // A call is only ever open over a referral.
    call: referral ? idOf(raw.call) : undefined,
  };
}

/** No tab in the URL: every campaign opens on its queue, whatever its status. */
export const DEFAULT_TAB: CampaignTab = 'queue';
